import { useWindowDimensions, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { DEFAULT_REGION_POLICIES } from '@ca/common'
import TextPaper from '@ui/elements/TextPaper'
import { TextPaperStatus } from '@ui/elements/TextPaper/types'
import useRWD from '@ui/theme/useRWD'
import { gap } from '@ui/theme/values'
import useCreateCustomerStore from './useCreateCustomerStore'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import useDateFormat from '@ui/hooks/useDateFormat'
import PolicyRow from '@user/customer/policies/PolicyRow'

import { CreateStepKey } from './types'
import {
  createStepValidationContext,
  getAddressDetailLines,
  getContactDetailLines,
  getInvoiceDetailLines,
  getPersonalDetailLines,
  getStepStatus,
  hasAnyDataFor,
  isKidsAccount,
} from './utils'

interface Props {
  onOpenStep: (step: CreateStepKey) => void
  errorSections?: Set<CreateStepKey>
}

export default function CreateCustomerDetails({
  onOpenStep,
  errorSections,
}: Props) {
  const { t, i18n } = useTranslation(['modals', 'deals-modals'])
  const { width: screenCurr } = useWindowDimensions()
  const {
    screen: { width: screenMax },
  } = useRWD()
  const config = useRemoteConfig()

  const containerProps = {
    style: {
      width: (screenCurr - 3 * gap) / 2,
      maxWidth: (screenMax - 3 * gap) / 2,
    },
  }

  const { data } = useCreateCustomerStore()
  const kids = isKidsAccount(data)
  const regionPolicies = config?.region.policies ?? DEFAULT_REGION_POLICIES
  const validationCtx = createStepValidationContext(
    data,
    regionPolicies,
    config?.region?.vat !== 'hidden'
  )
  const formatDate = useDateFormat({ dateStyle: 'medium' })

  const personalLines = getPersonalDetailLines(data, formatDate)
  const contactLines = getContactDetailLines(data)
  const addressLines = getAddressDetailLines(data, i18n.language)
  const invoiceLines = getInvoiceDetailLines(data)

  const statusFor = (step: CreateStepKey): TextPaperStatus => {
    if (errorSections?.has(step)) {
      return 'danger'
    }
    if (!hasAnyDataFor(step, data)) {
      return 'empty'
    }
    return getStepStatus(step, data, validationCtx)
  }

  const policiesStatus = statusFor('policies')

  return (
    <View className="flex-row gap-8 px-8">
      <View {...containerProps} className="flex flex-col gap-8">
        <TextPaper
          icon="user-circle"
          label={t('modals:personal.title')}
          lines={personalLines}
          status={statusFor('personal')}
          onPress={() => onOpenStep('personal')}
        />
        {!kids && (
          <TextPaper
            icon="home-2"
            label={t('modals:address.title')}
            lines={addressLines}
            status={statusFor('address')}
            onPress={() => onOpenStep('address')}
          />
        )}
        <TextPaper
          icon="license"
          label={t('modals:policies.title')}
          status={policiesStatus}
          onPress={() => onOpenStep('policies')}
        >
          {policiesStatus !== 'empty' && (
            <View className="gap-2">
              {regionPolicies.map((policy) => (
                <PolicyRow
                  key={policy.policy_key}
                  accepted={!!data.policies?.[policy.policy_key]}
                  label={t(`deals-modals:policies.${policy.policy_key}.title`)}
                />
              ))}
            </View>
          )}
        </TextPaper>
      </View>
      <View {...containerProps} className="flex flex-col gap-8">
        <TextPaper
          icon="message-circle-2"
          label={t('modals:contact.title')}
          lines={contactLines}
          status={statusFor('contact')}
          onPress={() => onOpenStep('contact')}
        />
        <TextPaper
          icon="file-invoice"
          label={t('modals:invoice.title')}
          lines={invoiceLines}
          status={statusFor('invoice')}
          onPress={() => onOpenStep('invoice')}
        />
      </View>
    </View>
  )
}
